import type { SelectOption } from '@/types/page'

export interface MasterField {
  key: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'number'
  required?: boolean
  maxLength?: number
  options?: SelectOption[]
  placeholder?: string
  help?: string
}

export interface MasterReference {
  module: string
  field: string
  mode: 'scalar' | 'array'
}

export interface MasterConfig {
  key: string
  pageId: string
  panel: string
  title: string
  description: string
  module: string
  nameKey: string
  createLabel: string
  usageLabel: string
  fields: MasterField[]
  references: MasterReference[]
  mergeTargetLabel: string
  disableHint: string
}

const STATUS: SelectOption[] = [
  { value: 'active', label: '启用', tone: 'success' },
  { value: 'disabled', label: '停用', tone: 'neutral' },
]

const DEVICE_TYPES = ['血压计', '血糖仪', '智能手环', '血氧仪', '体脂秤', '心电记录仪'].map(value => ({ value, label: value }))
const CAPABILITIES = ['血压', '血糖', '心率', '血氧', '体温', '体重', '心电'].map(value => ({ value, label: value }))

export const MASTERS: Record<string, MasterConfig> = {
  tags: {
    key: 'tags',
    pageId: 'users',
    panel: 'tags',
    title: '健康标签',
    description: '标签用于用户档案、消息受众与预警规则适用人群，停用前必须迁移全部引用。',
    module: 'health_tags',
    nameKey: 'name',
    createLabel: '新增标签',
    usageLabel: '关联用户',
    fields: [
      { key: 'name', label: '标签名称', type: 'text', required: true, maxLength: 20 },
      { key: 'description', label: '标签说明', type: 'textarea', maxLength: 120 },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'users', field: 'tags', mode: 'array' }],
    mergeTargetLabel: '目标标签',
    disableHint: '标签仍被用户引用时禁止停用，请先合并到其他标签。',
  },
  categories: {
    key: 'categories',
    pageId: 'drugs',
    panel: 'categories',
    title: '药品分类',
    description: '分类同时驱动药品筛选、用药计划可选药品与审核口径；合并后原分类停用。',
    module: 'drug_categories',
    nameKey: 'name',
    createLabel: '新增分类',
    usageLabel: '关联药品',
    fields: [
      { key: 'name', label: '分类名称', type: 'text', required: true, maxLength: 20 },
      { key: 'description', label: '分类说明', type: 'textarea', maxLength: 120 },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'drugs', field: 'category_name', mode: 'scalar' }],
    mergeTargetLabel: '合并到分类',
    disableHint: '分类仍被药品引用时禁止停用，请先合并到其他分类。',
  },
  models: {
    key: 'models',
    pageId: 'devices',
    panel: 'models',
    title: '设备型号',
    description: '型号维护设备类型与指标采集能力，设备绑定和健康监测量纲引用同一份主数据。',
    module: 'device_models',
    nameKey: 'name',
    createLabel: '新增型号',
    usageLabel: '在网设备',
    fields: [
      { key: 'name', label: '型号名称', type: 'text', required: true, maxLength: 40 },
      { key: 'model_code', label: '型号编码', type: 'text', required: true, maxLength: 24 },
      { key: 'device_type', label: '设备类型', type: 'select', options: DEVICE_TYPES, required: true },
      { key: 'capabilities', label: '采集能力', type: 'multiselect', options: CAPABILITIES, required: true },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'devices', field: 'model_name', mode: 'scalar' }],
    mergeTargetLabel: '迁移到型号',
    disableHint: '型号仍被设备引用时禁止停用，请先迁移全部设备。',
  },
  benefits: {
    key: 'benefits',
    pageId: 'membership-plans',
    panel: 'benefits',
    title: '会员权益目录',
    description: '权益可被多个会员方案复用；下线权益会影响新订单权益说明，需先迁移方案引用。',
    module: 'benefits',
    nameKey: 'name',
    createLabel: '新增权益',
    usageLabel: '引用方案',
    fields: [
      { key: 'name', label: '权益名称', type: 'text', required: true, maxLength: 30 },
      { key: 'description', label: '权益说明', type: 'textarea', maxLength: 160 },
      { key: 'sort', label: '排序', type: 'number' },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'membership_plans', field: 'benefit_names', mode: 'array' }],
    mergeTargetLabel: '迁移到权益',
    disableHint: '权益仍被会员方案引用时禁止停用，请先迁移引用。',
  },
  link_categories: {
    key: 'link_categories',
    pageId: 'links',
    panel: 'categories',
    title: '链接分类',
    description: '分类用于链接管理筛选与运营位归组，停用前必须先迁移链接。',
    module: 'link_categories',
    nameKey: 'name',
    createLabel: '新增分类',
    usageLabel: '关联链接',
    fields: [
      { key: 'name', label: '分类名称', type: 'text', required: true, maxLength: 20 },
      { key: 'sort', label: '排序', type: 'number' },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'links', field: 'category_name', mode: 'scalar' }],
    mergeTargetLabel: '合并到分类',
    disableHint: '分类仍被链接引用时禁止停用，请先合并。',
  },
  faq_categories: {
    key: 'faq_categories',
    pageId: 'content-faq',
    panel: 'categories',
    title: '帮助分类',
    description: '帮助分类用于用户端帮助中心分组与工单关联，停用前必须先迁移问题。',
    module: 'faq_categories',
    nameKey: 'name',
    createLabel: '新增分类',
    usageLabel: '关联问题',
    fields: [
      { key: 'name', label: '分类名称', type: 'text', required: true, maxLength: 20 },
      { key: 'sort', label: '排序', type: 'number' },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'faq', field: 'category_name', mode: 'scalar' }],
    mergeTargetLabel: '合并到分类',
    disableHint: '分类仍被帮助内容引用时禁止停用，请先合并。',
  },
  templates: {
    key: 'templates',
    pageId: 'health-reports',
    panel: 'templates',
    title: '报告模板',
    description: '报告模板控制指标口径与版式，生成中和历史报告保留触发时模板快照。',
    module: 'report_templates',
    nameKey: 'name',
    createLabel: '新增模板',
    usageLabel: '关联报告',
    fields: [
      { key: 'name', label: '模板名称', type: 'text', required: true, maxLength: 40 },
      { key: 'report_type', label: '报告类型', type: 'select', options: ['周报', '月报', '季度报告'].map(value => ({ value, label: value })), required: true },
      { key: 'description', label: '模板说明', type: 'textarea', maxLength: 160 },
      { key: 'status', label: '状态', type: 'select', options: STATUS, required: true },
    ],
    references: [{ module: 'reports', field: 'template_name', mode: 'scalar' }],
    mergeTargetLabel: '迁移到模板',
    disableHint: '模板仍被报告引用时禁止停用，请先迁移。',
  },
}

export function masterForPanel(pageId: string, panel: string): MasterConfig | undefined {
  return Object.values(MASTERS).find(config => config.pageId === pageId && config.panel === panel)
}

export function masterForPage(pageId: string): MasterConfig[] {
  return Object.values(MASTERS).filter(config => config.pageId === pageId)
}
